/* eslint-disable react/prop-types */
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getEmailToolEmails,
  saveEmailToolEmail,
} from "apis/controllers/emailTool";
import { CssTextField } from "qubCommon/Inputs/CssTextField";
import ActionConfirmDialog from "./components/ActionConfirmDialog";
import MailStatusChip from "./components/MailStatusChip";
import {
  EMAIL_ACTIONS,
  MESSAGE_STATUS_META,
} from "./mailToolConstants";
import {
  formatDateTime,
  getValue,
  getResponseMessage,
  isEditableStatus,
  isErrorResponse,
  normalizeEmailMessage,
  readResponseData,
} from "./mailToolUtils";

const statusOptions = [
  { value: "all", label: "Όλες οι καταστάσεις" },
  ...Object.entries(MESSAGE_STATUS_META).map(([value, meta]) => ({
    value,
    label: meta.label,
  })),
];

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const MailToolList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getEmailToolEmails({
        page: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearch || undefined,
      });
      if (isErrorResponse(response)) {
        enqueueSnackbar(getResponseMessage(response), { variant: "error" });
        setEmails([]);
        setPagination({
          total: 0,
          page: page + 1,
          pageSize: rowsPerPage,
          totalPages: 0,
        });
        return;
      }

      const data = readResponseData(response, {});
      const items = Array.isArray(data)
        ? data
        : getValue(data, "items", "Items") || [];
      const total = Array.isArray(data)
        ? data.length
        : getValue(data, "total", "Total") ?? items.length;
      const responsePage = getValue(data, "page", "Page") ?? page + 1;
      const responsePageSize =
        getValue(data, "pageSize", "PageSize") ?? rowsPerPage;
      const totalPages =
        getValue(data, "totalPages", "TotalPages") ||
        Math.ceil(total / responsePageSize);

      setEmails(Array.isArray(items) ? items.map(normalizeEmailMessage) : []);
      setPagination({
        total,
        page: responsePage,
        pageSize: responsePageSize,
        totalPages,
      });
    } catch (error) {
      enqueueSnackbar(getResponseMessage(error, "Δεν ήταν δυνατή η φόρτωση των μηνυμάτων"), {
        variant: "error",
      });
      setEmails([]);
      setPagination({
        total: 0,
        page: page + 1,
        pageSize: rowsPerPage,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, enqueueSnackbar, page, rowsPerPage]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [search]);

  const filteredEmails = useMemo(() => {
    return emails.filter((email) => {
      const matchesStatus =
        statusFilter === "all" || email.statusId === Number(statusFilter);

      return matchesStatus;
    });
  }, [emails, statusFilter]);
  const resultCountLabel =
    statusFilter === "all"
      ? `${pagination.total} αποτελέσματα`
      : `${filteredEmails.length} αποτελέσματα στη σελίδα`;

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      const response = await saveEmailToolEmail({
        id: deleteTarget.id,
        action: EMAIL_ACTIONS.DELETE,
        subject: deleteTarget.subject,
        body: deleteTarget.body,
        filters: null,
        recipients: [],
      });

      if (isErrorResponse(response)) {
        enqueueSnackbar(getResponseMessage(response), { variant: "error" });
        return;
      }

      enqueueSnackbar(getResponseMessage(response, "Το μήνυμα διαγράφηκε"), {
        variant: "success",
      });
      setDeleteTarget(null);
      if (emails.length === 1 && page > 0) {
        setPage((previousPage) => previousPage - 1);
      } else {
        loadEmails();
      }
    } catch (error) {
      enqueueSnackbar(getResponseMessage(error, "Δεν ήταν δυνατή η διαγραφή του μηνύματος"), {
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto", minHeight: "70vh" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <MailOutlineIcon color="primary" />
          <Box>
            <Typography variant="h3" fontWeight={700}>
              Εργαλείο ηλεκτρονικών μηνυμάτων
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pagination.total} αποθηκευμένα μηνύματα
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Ανανέωση">
            <span>
              <IconButton onClick={loadEmails} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/govnr/mail-tool/new")}
          >
            Νέο μήνυμα
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <CssTextField
            size="small"
            label="Αναζήτηση"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Αναζήτηση σε θέμα ή κείμενο"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { md: 360 } }}
          />
          <CssTextField
            select
            size="small"
            label="Κατάσταση"
            variant='standard'
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: { md: 220 } }}
            InputProps={{
              disableUnderline: true,

            }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CssTextField>
          <Chip
            label={resultCountLabel}
            color="primary"
            variant="outlined"
            sx={{ alignSelf: { xs: "flex-start", md: "flex-end" } }}
          />
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Θέμα</TableCell>
                <TableCell>Κατάσταση</TableCell>
                <TableCell>Δημιουργήθηκε</TableCell>
                <TableCell>Απεστάλη</TableCell>
                <TableCell>Παραλήπτες</TableCell>
                <TableCell>Πρόοδος</TableCell>
                <TableCell align="right">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                [1, 2, 3].map((row) => (
                  <TableRow key={row}>
                    <TableCell colSpan={7}>
                      <Skeleton height={34} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading &&
                filteredEmails.map((email) => {
                  const progressValue = email.recipientsTotal
                    ? Math.round((email.recipientsSent / email.recipientsTotal) * 100)
                    : 0;

                  return (
                    <TableRow key={email.id} hover>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {email.subject || "(Χωρίς θέμα)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <MailStatusChip statusId={email.statusId} />
                      </TableCell>
                      <TableCell>{formatDateTime(email.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(email.sentAt)}</TableCell>
                      <TableCell>{email.recipientsTotal}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack spacing={0.5}>
                          <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            color={
                              email.recipientsFailed > 0 ? "warning" : "primary"
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            {email.recipientsSent}/{email.recipientsTotal} απεστάλησαν
                            {email.recipientsFailed
                              ? `, ${email.recipientsFailed} απέτυχαν`
                              : ""}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            isEditableStatus(email.statusId, false)
                              ? "Επεξεργασία"
                              : "Προβολή"
                          }
                        >
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/govnr/mail-tool/${email.id}`)}
                          >
                            {isEditableStatus(email.statusId, false) ? (
                              <EditIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Διαγραφή">
                          <IconButton
                            color="error"
                            onClick={() => setDeleteTarget(email)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {!loading && filteredEmails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Δεν βρέθηκαν μηνύματα</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          labelRowsPerPage="Γραμμές ανά σελίδα:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} από ${count !== -1 ? count : `πάνω από ${to}`}`
          }
        />
      </Paper>

      <ActionConfirmDialog
        open={Boolean(deleteTarget)}
        title="Διαγραφή μηνύματος"
        description="Το μήνυμα θα αφαιρεθεί από τη λίστα του εργαλείου."
        confirmLabel="Διαγραφή"
        confirmColor="error"
        loading={deleteLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

export default MailToolList;
